import { createQueryKeys } from "@lukemorales/query-key-factory";
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";

import { useMutationWithError } from "@/hooks";
import { adminApi } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";

import type { ObjectiveTypeAccess } from "@/modules/performance/shared/constants";

import { type AdminObjectiveType } from "../types";

export type AdminObjectiveTypeList = Paginate<AdminObjectiveType>;

export const objectiveTypes = createQueryKeys(
  "admin-performance-objective-types",
  {
    all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
    one: (id?: number) => [id],
  },
);

const include = "children";

export const fetchObjectiveTypesQueryOptions = () =>
  queryOptions({
    ...objectiveTypes.all(),
    queryFn: () =>
      adminApi
        .get<AdminObjectiveType<true>[]>(`/performance/objectives/types`, {
          params: { include },
        })
        .then((response) => response.data),
  });

export const useFetchObjectiveTypes = () =>
  useQuery(fetchObjectiveTypesQueryOptions());

export const fetchObjectiveTypeQueryOptions = (id?: number) =>
  queryOptions({
    ...objectiveTypes.one(id),
    queryFn: () =>
      adminApi.get<AdminObjectiveType<true>>(
        `/performance/objectives/types/${id}`,
        {
          params: { include },
        },
      ),
  });

export const useFetchObjectiveType = (id?: number) =>
  useQuery(fetchObjectiveTypeQueryOptions(id));

export interface AdminObjectiveTypeRequest {
  types: (Partial<ObjectiveTypeAccess> & {
    id?: number | null;
    name: string;
    is_key_result_mandatory?: boolean;
    min_key_results?: number | null;
    max_key_results?: number | null;
    children: {
      id?: number | null;
      name: string;
    }[];
  })[];
}

export const useUpdateObjectiveType = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: AdminObjectiveTypeRequest) =>
      adminApi.put(`/performance/objectives/types`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: objectiveTypes.all._def,
      });
    },
  });
};
